import { useEffect, useState } from "react";
import { ArrowClockwise, DownloadSimple } from "@phosphor-icons/react";
import { api, API } from "../../lib/api";

const STATUS_STYLES = {
  new: "bg-accent/10 text-accent border-accent/30",
  contacted: "bg-primary/10 text-primary border-primary/30",
  closed: "bg-muted text-muted-foreground border-border",
};

function ValuationInput({ lead, onSaved }) {
  const [val, setVal] = useState(lead.valuation ?? "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const next = val === "" ? null : Number(val);
    if (next === (lead.valuation ?? null) || (val !== "" && Number.isNaN(next))) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/leads/${lead.id}`, { valuation: next });
      onSaved(data);
    } catch {
      // leave the input as-is; the admin can retry
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-foreground/40">$</span>
      <input
        data-testid={`admin-lead-valuation-${lead.id}`}
        type="number"
        min="0"
        step="100"
        value={val}
        placeholder="—"
        onChange={(e) => setVal(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
        className={`w-28 border border-border rounded-sm bg-transparent pl-7 pr-2 py-1.5 text-sm font-semibold text-foreground focus:outline-none focus:border-primary transition-colors duration-300 ${saving ? "opacity-50" : ""}`}
      />
    </div>
  );
}

export default function Enquiries() {
  const [leads, setLeads] = useState(null);
  const [error, setError] = useState("");

  const load = () => {
    api.get("/leads").then((r) => setLeads(r.data)).catch(() => setError("Could not load enquiries. Please try again."));
  };

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/leads/${id}`, { status });
      setLeads((prev) => prev.map((l) => (l.id === id ? data : l)));
    } catch {
      setError("Could not update status. Please try again.");
    }
  };

  const onValuationSaved = (updated) => {
    setLeads((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
  };

  return (
    <div data-testid="admin-enquiries">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl text-primary tracking-tight">Consultation enquiries</h1>
          <p className="mt-2 text-sm text-foreground/60" data-testid="admin-leads-count">
            {leads ? `${leads.length} enquir${leads.length === 1 ? "y" : "ies"}` : "Loading…"}
          </p>
        </div>
        <div className="flex items-center gap-5">
          <a
            data-testid="admin-export-csv"
            href={`${API}/leads/export`}
            className="flex items-center gap-2 bg-primary text-primary-foreground text-sm font-semibold px-5 py-2 rounded-full hover:bg-accent transition-colors duration-300"
          >
            <DownloadSimple size={15} weight="bold" /> Export CSV
          </a>
          <button
            data-testid="admin-enquiries-refresh"
            onClick={load}
            className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-primary transition-colors duration-300"
          >
            <ArrowClockwise size={15} weight="bold" /> Refresh
          </button>
        </div>
      </div>
      {error && <p className="mt-4 text-sm font-semibold text-destructive" data-testid="admin-error">{error}</p>}

      <div className="mt-10 bg-card border border-border rounded-sm overflow-x-auto" data-testid="admin-leads-table-wrap">
        <table className="w-full text-sm min-w-[1240px]" data-testid="admin-leads-table">
          <thead>
            <tr className="border-b border-border text-left">
              {["Received", "Name", "Contact", "Brand", "Situation", "Interest", "Valuation", "Message", "Status"].map((h) => (
                <th key={h} className="px-5 py-4 text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {leads && leads.length === 0 && (
              <tr>
                <td colSpan={9} className="px-5 py-14 text-center text-foreground/50 font-medium" data-testid="admin-leads-empty">
                  No enquiries yet — they'll appear here as soon as the first form is submitted.
                </td>
              </tr>
            )}
            {leads &&
              leads.map((l) => (
                <tr key={l.id} className="border-b border-border last:border-0 hover:bg-secondary/40 transition-colors duration-200" data-testid={`admin-lead-row-${l.id}`}>
                  <td className="px-5 py-4 whitespace-nowrap text-foreground/60">
                    {new Date(l.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                  </td>
                  <td className="px-5 py-4 font-semibold text-foreground whitespace-nowrap">{l.name}</td>
                  <td className="px-5 py-4 text-foreground/70">
                    <div>{l.email}</div>
                    {l.phone && <div className="text-foreground/45 text-xs">{l.phone}</div>}
                  </td>
                  <td className="px-5 py-4 text-foreground/70 whitespace-nowrap">{l.brand || "—"}</td>
                  <td className="px-5 py-4 capitalize text-foreground/70">{l.ownership_status}</td>
                  <td className="px-5 py-4 capitalize text-foreground/70">{l.interest}</td>
                  <td className="px-5 py-4">
                    <ValuationInput lead={l} onSaved={onValuationSaved} />
                  </td>
                  <td className="px-5 py-4 text-foreground/60 max-w-[200px] truncate" title={l.message}>{l.message || "—"}</td>
                  <td className="px-5 py-4">
                    <select
                      data-testid={`admin-lead-status-${l.id}`}
                      value={l.status}
                      onChange={(e) => setStatus(l.id, e.target.value)}
                      className={`text-xs font-bold uppercase tracking-wider border rounded-full px-3 py-1.5 bg-transparent cursor-pointer focus:outline-none ${STATUS_STYLES[l.status] || STATUS_STYLES.new}`}
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="closed">Closed</option>
                    </select>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
